import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import {
    faCog,
    faFileText,
    faSearch,
    faCopy,
    faBolt,
    faSave,
    faCheck,
    faComments,
    faArchive,
    faFolder,
    faUndo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@quyva/shared-ui';
import { useGlobalCredits } from '../context/GlobalStateContext.js';
import { getUrl } from '../utils/getUrl.js';

const ConfigureRepo = ({ repoToConfigure }) => {
    const [configType, setConfigType] = useState('prompt');
    const [selectedTask, setSelectedTask] = useState('qna');
    const { availableCustomModels } = useGlobalCredits();
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const defaultConfiguration = {
        models: {
            qna: 'Default Models Standard',
            extraction: 'Default Models Standard',
            file_additon: 'Default Models Standard',
        },
        prompts: {
            qna: '',
            extraction: '',
            file_additon: '',
            all: '',
        },
    };

    const [configuration, setConfiguration] = useState(defaultConfiguration);
    const [originalConfiguration, setOriginalConfiguration] =
        useState(defaultConfiguration);
    const [tempPrompt, setTempPrompt] = useState('');

    const configTypeConfig = {
        model: {
            icon: faCog,
            label: 'Model',
            color: 'text-buttons-primary',
            bg: 'bg-buttons-secondary',
            hover: 'hover:bg-buttons-secondary',
        },
        prompt: {
            icon: faFileText,
            label: 'Prompt',
            color: 'text-buttons-primary',
            bg: 'bg-buttons-secondary',
            hover: 'hover:bg-buttons-secondary',
        },
    };

    const taskConfig = {
        qna: {
            icon: faComments,
            label: 'Q&A',
            color: 'text-buttons-primary',
            bg: 'bg-buttons-secondary',
            hover: 'hover:bg-buttons-secondary',
        },
        extraction: {
            icon: faArchive,
            label: 'Extraction',
            color: 'text-buttons-primary',
            bg: 'bg-buttons-secondary',
            hover: 'hover:bg-buttons-secondary',
        },
        file_additon: {
            icon: faFileText,
            label: 'AddFile',
            color: 'text-buttons-primary',
            bg: 'bg-buttons-secondary',
            hover: 'hover:bg-buttons-secondary',
        },
        // all: {
        //     icon: faBolt,
        //     label: 'AllTasks',
        //     color: 'text-buttons-primary',
        //     bg: 'bg-buttons-secondary',
        //     hover: 'hover:bg-buttons-secondary',
        // },
    };

    const categoryColors = {
        'Default Models':
            'bg-buttons-secondary text-buttons-primary hover:bg-buttons-secondary',
        Gemini: 'bg-buttons-secondary text-buttons-primary hover:bg-buttons-secondary',
        OpenAI: 'bg-buttons-secondary text-buttons-primary hover:bg-buttons-secondary',
        Experimental:
            'bg-buttons-secondary text-buttons-primary hover:bg-buttons-secondary',
    };

    // Get API base URL
    const getApiUrl = () => {
        return getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/`
        );
    };

    // API call to fetch current configuration
    const fetchConfiguration = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${getApiUrl()}get_configuration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ repoToConfigure: repoToConfigure }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const fetchedConfig = {
                models: {
                    qna: data.models?.qna || 'Default Models Standard',
                    extraction:
                        data.models?.extraction || 'Default Models Standard',
                    file_additon:
                        data.models?.file_additon || 'Default Models Standard',
                },
                prompts: {
                    qna: data.prompts?.qna || '',
                    extraction: data.prompts?.extraction || '',
                    file_additon: data.prompts?.file_additon || '',
                    all: '',
                },
            };

            setConfiguration(fetchedConfig);
            setOriginalConfiguration(JSON.parse(JSON.stringify(fetchedConfig)));
            setHasChanges(false);
        } catch (error) {
            console.error('Error fetching configuration:', error);
            // Fallback to default configuration
            setConfiguration(defaultConfiguration);
            setOriginalConfiguration(
                JSON.parse(JSON.stringify(defaultConfiguration))
            );
        } finally {
            setLoading(false);
        }
    };

    // API call to save configuration
    const saveConfiguration = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${getApiUrl()}update_configuration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    repoToConfigure: repoToConfigure,
                    configuration: configuration,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            setOriginalConfiguration(JSON.parse(JSON.stringify(configuration)));
            setHasChanges(false);
            console.log('Configuration saved successfully:', result.message);
        } catch (error) {
            console.error('Error saving configuration:', error);
        } finally {
            setLoading(false);
        }
    };

    // API call to reset configuration
    const resetToDefault = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${getApiUrl()}reset_configuration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ repoToConfigure: repoToConfigure }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Update state with the reset configuration
            const resetConfig = {
                models: {
                    qna:
                        result.configuration.models?.qna ||
                        'Default Models Standard',
                    extraction:
                        result.configuration.models?.extraction ||
                        'Default Models Standard',
                    file_additon:
                        result.configuration.models?.file_additon ||
                        'Default Models Standard',
                },
                prompts: {
                    qna: result.configuration.prompts?.qna || '',
                    extraction: result.configuration.prompts?.extraction || '',
                    file_additon:
                        result.configuration.prompts?.file_additon || '',
                    all: '',
                },
            };

            setConfiguration(resetConfig);
            setOriginalConfiguration(JSON.parse(JSON.stringify(resetConfig)));
            setHasChanges(false);

            // Update temp prompt if in prompt mode
            if (configType === 'prompt') {
                setTempPrompt(resetConfig.prompts[selectedTask] || '');
            }

            console.log('Configuration reset successfully:', result.message);
        } catch (error) {
            console.error('Error resetting configuration:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check for changes
    useEffect(() => {
        const hasModelChanges =
            JSON.stringify(configuration.models) !==
            JSON.stringify(originalConfiguration.models);
        const hasPromptChanges =
            JSON.stringify(configuration.prompts) !==
            JSON.stringify(originalConfiguration.prompts);
        setHasChanges(hasModelChanges || hasPromptChanges);
    }, [configuration, originalConfiguration]);

    // Fetch configuration on mount
    useEffect(() => {
        fetchConfiguration();
    }, [repoToConfigure]);

    const handleModelChange = (model) => {
        if (selectedTask === 'all') {
            setConfiguration((prev) => ({
                ...prev,
                models: {
                    qna: model,
                    extraction: model,
                    file_additon: model,
                },
            }));
        } else {
            setConfiguration((prev) => ({
                ...prev,
                models: {
                    ...prev.models,
                    [selectedTask]: model,
                },
            }));
        }
    };

    const handlePromptChange = (value) => {
        setTempPrompt(value);
        if (selectedTask === 'all') {
            setConfiguration((prev) => ({
                ...prev,
                prompts: {
                    ...prev.prompts,
                    qna: value,
                    extraction: value,
                    file_additon: value,
                    all: value,
                },
            }));
        } else {
            setConfiguration((prev) => ({
                ...prev,
                prompts: {
                    ...prev.prompts,
                    [selectedTask]: value,
                },
            }));
        }
    };

    const handleSave = () => {
        saveConfiguration();
    };

    const handleReset = () => {
        resetToDefault();
    };

    useEffect(() => {
        if (configType === 'prompt') {
            setTempPrompt(configuration.prompts[selectedTask] || '');
        }
    }, [selectedTask, configType, configuration.prompts]);

    const getCurrentModel = () => configuration.models[selectedTask];

    const ModelBubbles = () => {
        const selectedModel = getCurrentModel();

        // Create the model list with Default Models on top, then other categories
        const modelList = {
            'Default Models': ['Standard', 'Precision'],
            ...(availableCustomModels || {}),
        };

        return (
            <div className='space-y-2 '>
                {Object.entries(modelList).map(([category, models]) => (
                    <div key={category} className='flex items-center gap-2'>
                        <div className='text-xs font-medium text-gray-600 w-20 shrink-0'>
                            {category}
                        </div>
                        <div className='flex flex-wrap gap-1'>
                            {models.map((model) => {
                                const fullModel = `${category} ${model}`;
                                const isSelected = selectedModel === fullModel;
                                return (
                                    <button
                                        key={model}
                                        onClick={() =>
                                            handleModelChange(fullModel)
                                        }
                                        className={`px-2.5 py-0.5 text-xs font-medium rounded-full transition-all duration-200 ${
                                            categoryColors[category]
                                        } ${
                                            isSelected
                                                ? 'ring-2 ring-buttons-primary'
                                                : ''
                                        }`}
                                    >
                                        {model}
                                        {isSelected && (
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className='ml-1 w-3 h-3'
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const Toggle = ({ isToggled, onToggle }) => (
        <div className='flex items-center gap-2'>
            <span
                className={`text-xs font-medium ${
                    !isToggled ? 'text-buttons-primary' : 'text-gray-500'
                }`}
            >
                Model
            </span>
            <button
                onClick={onToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isToggled ? 'bg-buttons-primary' : 'bg-buttons-primary'
                }`}
            >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isToggled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
            </button>
            <span
                className={`text-xs font-medium ${
                    isToggled ? 'text-buttons-primary' : 'text-gray-500'
                }`}
            >
                Prompt
            </span>
        </div>
    );

    if (loading) {
        return (
            <div className='flex-1 w-[80vw] lg:w-[50vw] h-[50vh] bg-white rounded-md font-sans text-xs flex items-center justify-center border border-gray-200'>
                <div className='text-gray-500'>Loading configuration...</div>
            </div>
        );
    }

    return (
        <div className='flex-1 w-[80vw] lg:w-[50vw] max-h-[70vh] bg-white rounded-md font-sans text-xs flex flex-col border border-gray-200 overflow-hidden'>
            {/* Combined Navbar */}
            <div className='flex gap-2 p-2 rounded-md mb-2 justify-start relative '>
                <div className='items-center hidden'>
                    <Toggle
                        isToggled={configType === 'prompt'}
                        onToggle={() =>
                            setConfigType(
                                configType === 'model' ? 'prompt' : 'model'
                            )
                        }
                    />
                </div>
                <div className='flex gap-1'>
                    {Object.entries(taskConfig).map(
                        ([task, { icon, label, color, bg, hover }]) => {
                            return (
                                <button
                                    key={task}
                                    onClick={() => setSelectedTask(task)}
                                    className={`flex-1 p-1.5 rounded-md transition-all duration-200 ${
                                        selectedTask === task
                                            ? `${bg} ${color}`
                                            : `text-gray-500 ${hover}`
                                    }`}
                                    title={label}
                                >
                                    <FontAwesomeIcon
                                        icon={icon}
                                        className='w-4 h-4 mx-auto'
                                    />
                                    <span className='text-[10px] mt-0.5 block'>
                                        {label}
                                    </span>
                                </button>
                            );
                        }
                    )}
                </div>
            </div>

            {/* Configuration Interface */}
            <div className='flex-1 p-2 overflow-hidden'>
                {configType === 'model' ? (
                    <div>
                        <div className='text-xs font-medium text-gray-700 mb-4'>
                            {taskConfig[selectedTask].label} Model
                        </div>
                        <ModelBubbles />
                        {selectedTask === 'all' && (
                            <div className='mt-1.5 text-[10px] text-buttons-primary bg-buttons-secondary p-1 rounded-lg'>
                                Applies to all tasks
                            </div>
                        )}
                    </div>
                ) : (
                    <div className='space-y-1.5 h-full flex flex-col'>
                        <div className='text-xs font-medium text-gray-700'>
                            {taskConfig[selectedTask].label} Prompt
                        </div>
                        <textarea
                            value={tempPrompt}
                            onChange={(e) => {
                                if (
                                    taskConfig[selectedTask].label !==
                                    taskConfig['file_additon'].label
                                ) {
                                    handlePromptChange(e.target.value);
                                }
                            }}
                            placeholder={`Custom prompt for ${taskConfig[
                                selectedTask
                            ].label.toLowerCase()}...`}
                            className={`flex-1 ${
                                taskConfig[selectedTask].label ==
                                taskConfig['file_additon'].label
                                    ? 'disabled:opacity-50 bg-gray-100'
                                    : 'focus:ring-1 focus:ring-buttons-primary'
                            } p-1.5 border border-gray-200 rounded-lg resize-none focus:outline-none  text-xs placeholder-gray-400 transition-all duration-200`}
                        />
                        {selectedTask === 'all' && (
                            <div className='mt-1.5 text-[10px] text-buttons-primary bg-buttons-secondary p-1 rounded-lg'>
                                Applies to all tasks
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className='p-2 flex gap-2'>
                <Button
                    type='secondary'
                    icon={faUndo}
                    text='Reset to Default'
                    title='Reset to default configuration'
                    className='flex-1 text-gray-600 bg-gray-100 hover:bg-gray-200'
                    onClick={handleReset}
                    disabled={loading}
                />
                <div className='flex-1'></div>
                <Button
                    type='primary'
                    icon={faSave}
                    text='Save'
                    title='Save configuration'
                    className={`flex-1 ${
                        hasChanges && !loading
                            ? 'text-buttons-primary bg-buttons-secondary'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    }`}
                    onClick={handleSave}
                    disabled={!hasChanges || loading}
                />
            </div>
        </div>
    );
};

export default ConfigureRepo;
